import { FC, ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyDocumentLanguage, fetchLanguages, isI18nReady, missingLanguages } from "../../res/languages/i18nReady";
import { BootLoader } from "./BootLoader/BootLoader";

/** How long the app waits for its texts before showing anyway (a stuck network, not a slow one). */
export const I18N_BOOT_TIMEOUT_MS = 15000;

/**
 * Holds the whole app until the active language's texts are in memory.
 *
 * The locale JSON is fetched after the JS starts; rendering before it lands shows raw
 * keys ("contacts.title") for a moment on every page — old frame, new frame and the
 * public site alike. Until it lands this shows the boot loader (the same look as the
 * static #boot-splash in public/index.html, which stays up until the gate opens, so the
 * user sees one continuous loading screen and then the page with its words).
 *
 * If the first fetch failed, the files are requested once more. A deadline makes sure a
 * dead network never leaves the user on the loader forever. Like PersistBoot, `ready`
 * is one-way: once the children mount they are never unmounted. Language switches after
 * that are handled by installSafeLanguageSwitch (the current words stay until the new
 * language is loaded).
 */
export const I18nGate: FC<{ children: ReactNode; timeoutMs?: number }> = ({
  children,
  timeoutMs = I18N_BOOT_TIMEOUT_MS,
}) => {
  const { i18n } = useTranslation(undefined, { useSuspense: false });
  const [ready, setReady] = useState(() => isI18nReady(i18n));

  useEffect(() => {
    if (ready) return;
    let retried = false;
    let alive = true;

    const check = () => {
      if (!alive) return;
      if (isI18nReady(i18n)) {
        setReady(true);
        return;
      }
      // init finished but a file failed to load: ask once more, then show what we have.
      if (i18n.isInitialized && !retried) {
        retried = true;
        void fetchLanguages(i18n, missingLanguages(i18n, i18n.language)).then(() => {
          if (alive) setReady(true);
        });
      }
    };

    check();
    i18n.on("initialized", check);
    i18n.on("loaded", check);
    i18n.on("failedLoading", check);
    i18n.on("languageChanged", check);

    const timer = setTimeout(() => {
      console.warn(`[i18n] Texts not loaded within ${timeoutMs}ms — showing the app anyway.`);
      setReady(true);
    }, timeoutMs);

    return () => {
      alive = false;
      i18n.off("initialized", check);
      i18n.off("loaded", check);
      i18n.off("failedLoading", check);
      i18n.off("languageChanged", check);
      clearTimeout(timer);
    };
  }, [ready, i18n, timeoutMs]);

  // Before paint, in the same commit that mounts the app: the splash goes away and the
  // first frame the user sees already has its words.
  // <html dir/lang> live here: this component re-renders on languageChanged together with
  // every translated child (React batches them into one commit), and a layout effect runs
  // before paint — so the direction flips in the same frame as the words, on the first
  // load and on every switch (Hebrew is RTL from its very first frame).
  const lang = i18n.language;
  useLayoutEffect(() => {
    if (!ready) return;
    applyDocumentLanguage(lang);
    document.getElementById("boot-splash")?.remove();
  }, [ready, lang]);

  return ready ? <>{children}</> : <BootLoader />;
};

export default I18nGate;
